<template>
  <login-panel class="sign-in" xs>
    <div class="sign-in__logo">
      <img src="/static/imgs/lg-treetech.png" alt="Treetech logo"/>
    </div>
    <div role="form" class="sign-in__form">
      <div class="form-row sign-in__form-row">
        <label for="username" class="sign-in__label">
          {{ $t('login.username') }}
        </label>
        <b-form-input
          id="username"
          type="text"
          v-model="username"
          :state="$bsStateValidator($v.username.$error)"
          @input="$v.username.$touch()"
          @keyup.enter.native="login"
          maxlength="20"
        ></b-form-input>
      </div>
      <div class="form-row sign-in__form-row">
        <label for="password" class="sign-in__label">
          {{ $t('login.password') }}
        </label>
        <password-eye
          id="password"
          type="password"
          v-model="password"
          :state="$bsStateValidator($v.password.$error)"
          @input="$v.password.$touch()"
          @keyup.enter.native="dispatchLoginClick"
          maxlength="20"
        />
      </div>
      <div class="form-row sign-in__form-row">
        <captcha ref="captcha" v-if="showCaptcha"/>
      </div>
      <div class="form-row sign-in__form-row">
        <s-loading-button
          ref="btnSignin"
          id="signin"
          class="sign-in__btn-sign-in"
          @click="$event.wait(login())"
          variant="primary"
          block>
            {{ $t('login.signin') }}
        </s-loading-button>
      </div>
    </div>
    <div class="sign-in__text-links">
      <router-link :to="{ name: 'externo' }">
        {{ $t('login.alternative-authentication-method') }}
      </router-link>
      <router-link :to="{ name: 'insert-user-data' }">
        {{ $t('login.forgot-my-password') }}
      </router-link>
    </div>
    <expired-session-modal
      :visible.sync="showModalSessionExpired"
      @close="closeModalSessionExpired"
    />
  </login-panel>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import ExpiredSessionModal from '@/components/ExpiredSessionModal';
import PasswordEye from '@/components/inputs/PasswordEye';
import { grammaticalJoin } from '@/services/arrayUtils';
import commonCaptchaService from '@/services/security/common';
import AuthenticationService from '@/services/authentication';
import SignInService from '@/domain/services/login/signIn';
import LoginPanel from '../../../components/LoginPanel';

export default {
  components: {
    LoginPanel,
    ExpiredSessionModal,
    captcha: () => import(/* webpackChunkName: "captcha" */ '@/components/captcha/Captcha'),
    PasswordEye,
  },
  props: {},
  validations: {
    username: {
      required,
    },
    password: {
      required,
    },
  },
  data() {
    return {
      username: null,
      password: null,
      showCaptcha: false,
      showModalSessionExpired: false,
    };
  },
  methods: {
    login() {
      if (this.$v.$invalid) {
        this.$pushAlert({
          code: 'MA001',
          messageParams: {
            NomeDoCampo: this.getEmptyFields(),
          },
        });
        return Promise.reject();
      }

      if (!this.showCaptcha) {
        return this.authenticate();
      }
      const { captcha } = this.$refs;
      return captcha.validateCaptcha()
        .then(this.authenticate)
        .catch(captcha.getCaptcha);
    },
    getEmptyFields() {
      const result = [];

      if (!this.$v.username.required) {
        result.push(this.$t('login.username'));
      }

      if (!this.$v.password.required) {
        result.push(this.$t('login.password'));
      }

      return grammaticalJoin(result);
    },
    authenticate() {
      return AuthenticationService.login({ username: this.username, password: this.password })
        .then((signInData) => {
          SignInService.proceedAfterLogin(signInData);
        }).catch(this.catchHandle);
    },
    catchHandle(error) {
      if (error && error.response && error.response.status === 400) {
        this.showOrHideCaptcha(error);
        return Promise.resolve();
      }

      return Promise.reject(error);
    },
    showOrHideCaptcha(error) {
      const errorData = commonCaptchaService.extractErrorInformation(error);

      if (commonCaptchaService.shouldShowCaptcha(errorData.blockedUser, errorData.wrongAttempts)) {
        if (this.showCaptcha) {
          this.$refs.captcha.getCaptcha();
        }
        this.showCaptcha = true;
      } else {
        this.showCaptcha = false;
      }
    },
    dispatchLoginClick() {
      this.$refs.btnSignin.$el.click();
    },
    closeModalSessionExpired() {
      this.showModalSessionExpired = false;
    },
  },
  created() {
    if (this.$route.params.hasTimedOut) {
      this.showModalSessionExpired = true;
    }
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mixins"

.sign-in
  display: flex
  flex-direction: column
  max-width: 320px
  background-color: $color-white-100
  +md-box-shadow-3
  &__error-message
    background-color: var(--color-background-danger)
    border: 1px solid $color-danger
    width: 320px
    margin: 10px auto
    color: $color-danger
    h3
      font-weight: bold
  &__logo
    text-align: center
    height: 90px
    margin: 15px
    img
      height: 100%
      width: auto
  &__form-row
    margin-top: 20px
    display: flex
    flex-direction: column
  &__label
    font-weight: 600
  &__btn-sign-in
    margin-top: 10px
    font-weight: 600
  &__text-links
    display: flex
    flex-direction: column
    margin: 20px 0 20px 0
    gap: 8px
    text-transform: uppercase
    text-decoration: underline
    color: $color-link
    font-size: .85em
    text-align: center
</style>
